import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Cpu, ShieldCheck, Zap, Sparkles, Activity } from "lucide-react";
import Particles from "@/components/Particles";
import SEO from "@/components/SEO";
import { HERO, MACHINES_PAGE } from "@/constants/testIds";
import { fetchMachines, fetchCases, resolveMediaUrl } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { organizationLd, websiteLd, faqLd, COMMON_FAQS } from "@/lib/jsonLd";

const HERO_VIDEO = "https://customer-assets.emergentagent.com/job_f69d83c4-41ad-4b7c-bf2f-c53270d2d86c/artifacts/xlwknqkp_DermaFacial%20-3D_360.mp4";
const TECH_VIDEO = "https://customer-assets.emergentagent.com/job_f69d83c4-41ad-4b7c-bf2f-c53270d2d86c/artifacts/m2oofhbi_DermaFacial%203D%20light%20effect.mp4";

export default function Home() {
  const [machines, setMachines] = useState([]);
  const [cases, setCases] = useState([]);
  const { t } = useT();

  useEffect(() => {
    fetchMachines().then(setMachines).catch(() => {});
    fetchCases().then(setCases).catch(() => {});
  }, []);

  return (
    <div>
      <SEO
        title="Asirox · Equipo y Plataformas Dermo Faciales Profesionales Premium | Aparatología Estética"
        description="Asirox fabrica equipos dermo faciales profesionales premium para clínicas estéticas, centros de belleza y franquicias: hidradermoabrasión, radiofrecuencia, ultrasonidos, microcorrientes y microneedling. Más de 20 años de Conscious Engineering."
        keywords="equipos dermo faciales, aparatología dermo facial profesional, plataforma dermo facial, hidradermoabrasión profesional, radiofrecuencia facial, ultrasonidos facial, microcorrientes faciales, equipos clínicas estéticas, aparatología estética premium, Asirox, Préime DermaFacial"
        jsonLd={[organizationLd(), websiteLd(), faqLd(COMMON_FAQS)]}
      />
      {/* HERO CINEMATOGRÁFICO (dark) */}
      <section className="relative h-[100vh] min-h-[720px] overflow-hidden video-vignette -mt-20 dark-section">
        <video
          autoPlay loop muted playsInline
          className="absolute inset-0 w-full h-full object-cover"
          poster="https://images.pexels.com/photos/8442034/pexels-photo-8442034.jpeg"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        <Particles count={28} />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-24">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="overline"
          >
            {t("hero.tagline")}
          </motion.p>
          <motion.h1
            data-testid={HERO.title}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="font-heading text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-extrabold tracking-tighter leading-[0.95] mt-6 max-w-5xl text-white"
          >
            {t("hero.title_a")} <br/>
            <span className="ink-heading">{t("hero.title_highlight")}</span> {t("hero.title_b")}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.8 }}
            className="text-white/85 text-lg md:text-xl mt-8 max-w-2xl leading-relaxed font-body font-light"
          >
            {t("hero.subtitle")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="flex flex-col sm:flex-row gap-4 mt-10"
          >
            <Link to="/contacto?intent=demo" data-testid={HERO.cta_demo} className="btn-primary">
              {t("hero.cta_demo")} <ArrowUpRight size={18} />
            </Link>
            <Link to="/equipos-dermo-faciales" data-testid={HERO.cta_catalog} className="btn-secondary btn-on-dark">
              {t("hero.cta_catalog")}
            </Link>
            <Link to="/contacto?intent=quote" data-testid={HERO.cta_quote} className="btn-secondary btn-on-dark">
              {t("hero.cta_quote")}
            </Link>
          </motion.div>
        </div>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 scroll-indicator" data-testid={HERO.scroll_indicator}>
          <div className="w-px h-12 bg-gradient-to-b from-[#ff9c00] to-transparent" />
        </div>
      </section>

      {/* TRUST STRIPE */}
      <section className="py-8 bg-[#f1e3cb] border-y border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { k: "+20", v: t("home.stat_years") },
            { k: "50.000+", v: t("home.stat_shots") },
            { k: "CE IIb", v: t("home.stat_cert") },
            { k: "27", v: t("home.stat_countries") },
          ].map((s) => (
            <div key={s.v}>
              <p className="metric-num text-3xl md:text-4xl">{s.k}</p>
              <p className="overline mt-2">{s.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED MACHINES — BENTO (light) */}
      <section className="relative py-24 md:py-32" data-testid={MACHINES_PAGE.container}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16">
            <div className="md:col-span-7">
              <p className="overline">{t("home.sec01")}</p>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter mt-4 ink-heading">
                {t("home.sec01_title_a")} <span className="text-black">{t("home.sec01_title_highlight")}</span>
              </h2>
            </div>
            <p className="md:col-span-5 text-black/75 text-base leading-relaxed">
              {t("home.sec01_text")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {machines.map((m, idx) => {
              const span = idx === 0 ? "md:col-span-8" : idx === 1 ? "md:col-span-4" : idx === 2 ? "md:col-span-5" : "md:col-span-7";
              return (
                <Link
                  key={m.slug}
                  to={`/equipos-dermo-faciales/${m.slug}`}
                  data-testid={MACHINES_PAGE.card(m.slug)}
                  className={`group relative ${span} card-tilt border border-black/15 overflow-hidden bg-black aspect-[4/3] md:aspect-[5/4]`}
                >
                  <img src={resolveMediaUrl(m.hero_image)} alt={m.name} className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700"/>
                  <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black"/>
                  <div className="relative h-full flex flex-col justify-between p-7 md:p-10">
                    <div>
                      <p className="overline overline-on-dark">{m.category}</p>
                      <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mt-3 max-w-md text-white">{m.name}</h3>
                    </div>
                    <div>
                      <p className="text-white/80 text-sm md:text-base max-w-md leading-relaxed">{m.tagline}</p>
                      <div className="flex items-center gap-2 mt-5 text-white">
                        <span className="text-sm font-semibold">{t("common.view_tech")}</span>
                        <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"/>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONSCIOUS ENGINEERING */}
      <section className="relative py-24 md:py-32 bg-[#f1e3cb] border-y border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <p className="overline">{t("home.sec02")}</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter mt-4 ink-heading">
              {t("home.sec02_title_a")} <span className="text-black">{t("home.sec02_title_highlight")}</span> {t("home.sec02_title_b")}
            </h2>
            <p className="text-black/80 mt-6 leading-relaxed">{t("home.sec02_text")}</p>
            <div className="grid grid-cols-2 gap-4 mt-10">
              {[
                { icon: Cpu, t: "Software SMART IoT", d: "Protocolos en la nube, actualizaciones OTA." },
                { icon: ShieldCheck, t: "Seguridad médica", d: "CE IIb · FDA 510(k) · ISO 13485." },
                { icon: Zap, t: "Alta potencia real", d: "Hasta 2000W pico, sin caídas en cabina." },
                { icon: Activity, t: "Datos en tiempo real", d: "Cada disparo registrado y trazable." },
              ].map((f) => (
                <div key={f.t} className="border border-black/15 bg-[#f7ebd9] p-5 card-tilt">
                  <f.icon size={20} strokeWidth={1.6} className="text-[#3D1F0A]"/>
                  <p className="font-heading font-bold mt-3 ink-heading">{f.t}</p>
                  <p className="text-sm text-black/70 mt-2 leading-relaxed">{f.d}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7 relative aspect-[4/3] overflow-hidden border border-black/15 video-vignette bg-black">
            <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover">
              <source src={TECH_VIDEO} type="video/mp4" />
            </video>
            <div className="absolute top-6 left-6 z-10 overline overline-on-dark">Asirox Tech Lab</div>
            <div className="absolute bottom-6 left-6 right-6 z-10">
              <p className="text-xs text-white/60 font-mono">REC · 4K · {new Date().toISOString().slice(0,10)}</p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTORS PREVIEW */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <div>
              <p className="overline">{t("home.sec03")}</p>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter mt-4 ink-heading">{t("home.sec03_title")}</h2>
            </div>
            <Link to="/sectores-aparatologia-estetica" className="text-[#3D1F0A] link-underline text-sm self-start md:self-auto font-semibold">
              {t("home.sec03_view_all")}
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { t: "Clínicas estéticas", d: "Equipos médicos con trazabilidad y rendimiento clínico." },
              { t: "Centros de belleza", d: "Operación simple, protocolos guiados, alta rentabilidad." },
              { t: "Franquicias", d: "Estandarización, formación, soporte multi-sede." },
              { t: "Medicina estética", d: "Plataformas médicas para dermatólogos y MED-spas." },
            ].map((s, i) => (
              <div key={s.t} className="border border-black/15 p-6 card-tilt h-full bg-[#f7ebd9]">
                <p className="overline">0{i+1}</p>
                <p className="font-heading text-xl font-bold mt-4 ink-heading">{s.t}</p>
                <p className="text-black/70 text-sm mt-2 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CASES STRIP */}
      <section className="py-24 md:py-32 bg-[#f1e3cb] border-y border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-7">
              <p className="overline">{t("home.sec04")}</p>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter mt-4 ink-heading">
                {t("home.sec04_title_a")} <span className="text-black">{t("home.sec04_title_highlight")}</span> {t("home.sec04_title_b")}
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {cases.slice(0,4).map((c) => (
              <div key={c.id} className="border border-black/15 bg-[#f7ebd9] p-8 card-tilt relative overflow-hidden">
                <p className="metric-num text-4xl md:text-5xl">{c.metric_headline}</p>
                <p className="text-black/80 mt-6 italic">"{c.quote}"</p>
                <div className="div-line my-6"/>
                <div className="flex justify-between items-end text-xs text-black/60 font-mono">
                  <span>{c.clinic_name}</span>
                  <span className="text-[#3D1F0A]">{c.machine}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ VISIBLE (refuerza FAQPage schema para citas en ChatGPT/Perplexity) */}
      <section className="py-24 md:py-32 light-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <p className="overline">Preguntas frecuentes</p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter mt-6 ink-heading">
            Lo que más nos <span className="text-[#3D1F0A]">preguntan.</span>
          </h2>
          <div className="mt-12 space-y-4" data-testid="home-faq">
            {COMMON_FAQS.map((f, i) => (
              <details key={i} className="group border border-black/15 bg-[#f7ebd9] p-6 cursor-pointer card-tilt" data-testid={`faq-item-${i}`}>
                <summary className="flex items-start justify-between gap-4 list-none">
                  <h3 className="font-heading text-lg md:text-xl font-bold ink-heading flex-1">{f.q}</h3>
                  <span className="text-[#3D1F0A] text-2xl leading-none transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="text-black/80 mt-4 leading-relaxed text-sm md:text-base">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-32 dark-section overflow-hidden">
        <Particles count={20}/>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="overline">{t("home.sec05")}</p>
          <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter mt-6 text-white">
            {t("home.cta_title_a")} <span className="ink-heading">{t("home.cta_title_highlight")}</span>
          </h2>
          <p className="text-white/75 mt-8 max-w-xl mx-auto leading-relaxed">
            {t("home.cta_text")}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-10">
            <Link to="/contacto?intent=demo" className="btn-primary">
              {t("home.cta_book")} <ArrowUpRight size={18}/>
            </Link>
            <Link to="/asesor-dermo-facial-ia" className="btn-secondary btn-on-dark">
              <Sparkles size={16} className="text-white"/> {t("home.cta_ai")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
